import { useEffect, useRef, useState } from "react";
import { DateRangePicker as RSuiteDateRangePicker } from "rsuite";
import type { IPreDefinedRange } from "../../types/date_range_picker";
import type { IDateRangePicker } from "../../types/date_range_picker";
import type { IButton } from "../../types/Button/button";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useScreenWidth } from "../../hooks/useScreenWidth";
import { arraySorter as dateSorter } from "../../utils/arraySorter";
import { formatDateRange, MIN_DATE, TODAY } from "../../utils/dateRangePicker";
import { DateRangePickerButton } from "./DateRangePickerButton";
import { DateRangePickerMenu } from "./DateRangePickerMenu";
import styles from "./date-range-picker.module.scss";

const MOBILE_BREAKPOINT = 900;

const { allowedRange } = RSuiteDateRangePicker;

export const DateRangePicker = (
  {
    lang = 'en',
    width = 'fit-content',
    horizontalPlacement,
    verticalPlacement = 'bottom',
    label,
    placeholder,
    text,
    minDate = MIN_DATE,
    maxDate = TODAY,
    value,
    disabledDate = allowedRange(minDate, maxDate),
    disabled = false,
    resetable = false,
    onChange,
    onApply,
    onClear,
    disabledApplyBtn = false,
    customLocale,
    preDefinedRanges,
    jumpToStartDateOnShortcutClick = false,
    closeOnClickOutside = true,
    iconOnlyTrigger,
    isMobile,
    hasBackdrop,
    containerRef,
    shouldSelectOneDate = false,
  }: IDateRangePicker
) => {

  const rangePickerBtnRef = useRef<any>();
  const rangePickerMenuRef = useRef<any>();

  const screenWidth = useScreenWidth();
  const isMobileBreakpoint = isMobile || (screenWidth <= MOBILE_BREAKPOINT);

  const defaultPreDefinedRange: IPreDefinedRange | undefined = preDefinedRanges?.find(range => range.default);
  const defaultRange: [Date, Date] | undefined = value || defaultPreDefinedRange?.selectionRange || [TODAY, TODAY];

  const [isOpen, setIsOpen] = useState(false);
  const [isResetable, setIsResetable] = useState(false);

  // Custom selection (not from the pre-defined ranges)
  const [customSelectionRange, setCustomSelectionRange] = useState<[Date] | [Date, Date] | []>([]);

  // Selected range that visually appears to user
  const [selectedRange, setSelectedRange] = useState<[Date, Date] | undefined>(defaultRange);

  // Applied ranges (their data appears in the picker toggle button)
  const [appliedRange, setAppliedRange] = useState<[Date, Date] | undefined>(defaultRange);

  const [selectedPreDefinedRange, setSelectedPreDefinedRange] = useState<IPreDefinedRange | undefined>(defaultPreDefinedRange);

  const [appliedPreDefinedRange, setAppliedPreDefinedRange] = useState<IPreDefinedRange | undefined>(defaultPreDefinedRange);


  const getPreDefinedRanges = (selectedPreRangeIndex?: number) => {

    const preDefinedRange = preDefinedRanges?.map(range => {

      const [startDate, endDate] = appliedRange || [];

      // setting the dates of the (LAST APPLIED) range in case of 'custom' range selected by default
      const selectRange =
        range.name === 'custom'
          ?
          [startDate, endDate]
          :
          range.selectionRange;

      const onClickAction = () => {
        // check if the user clicks on already selected range ==> do nothing
        if (selectedPreDefinedRange?.name === range.name) return;

        // check if the user selects 'custom' range after selecting another range,
        // selection range set to undefined to let the user selects the range freely
        if (selectedPreDefinedRange?.name !== 'custom' && range.name === 'custom') {
          setSelectedPreDefinedRange({
            ...range,
            selectionRange: undefined as any
          });

          setSelectedRange(undefined);
        } else {
          // set the selected range with the related range normally
          setSelectedPreDefinedRange(range);
          setSelectedRange(range.selectionRange);
        }
      }

      return {
        ...range,
        selectionRange: selectRange as [Date, Date],
        selected: range.index === selectedPreRangeIndex,
        onClickHandler: onClickAction
      } as IPreDefinedRange
    })

    return preDefinedRange;
  };

  useEffect(() => {
    if (preDefinedRanges) {
      const relatedRange = preDefinedRanges?.find(range => range.selected);

      setSelectedPreDefinedRange(relatedRange);
      setAppliedPreDefinedRange(relatedRange);
      setSelectedRange(relatedRange?.selectionRange);
      setAppliedRange(relatedRange?.selectionRange);
    }
  }, [preDefinedRanges]);

  useEffect(() => {
    // When the picker is closed, it will retain the same selected range,
    // except for the 'custom' range, it will display the dates of the (LAST APPLIED) range in the main picker
    setSelectedRange(appliedRange);

    if (!isOpen && preDefinedRanges) {
      const relatedRange = preDefinedRanges?.find(range => range.index === appliedPreDefinedRange?.index) as IPreDefinedRange;

      setSelectedPreDefinedRange({
        ...relatedRange,
        ...(
          relatedRange?.name === 'custom'
            ?
            {
              selectionRange: appliedRange,
            }
            :
            {}
        )
      })

      setCustomSelectionRange([]);
    }
  }, [isOpen]);

  useEffect(() => {
    // When user selects dates manually the selection range become 'custom'
    // with the selected range dates (not the default custom 'selectionRange')
    const relatedRange = preDefinedRanges?.find(range => range.name === 'custom');

    if (customSelectionRange.length === 1) {

      setSelectedPreDefinedRange(relatedRange);

    } else if (customSelectionRange.length === 2) {

      setSelectedPreDefinedRange(
        relatedRange ?
          {
            ...relatedRange,
            selectionRange: customSelectionRange,
          }
          :
          undefined
      );

      setSelectedRange(customSelectionRange);
    }

  }, [customSelectionRange]);

  useEffect(() => {
    // Resets the manual selection state if the user selects a range other than 'custom'
    selectedPreDefinedRange?.name !== 'custom' && customSelectionRange.length &&
      setCustomSelectionRange([]);

    onChange?.(selectedRange);
  }, [selectedRange]);

  const toggleRangePicker = () => {
    setIsOpen(!isOpen);
  };

  const closeRangePicker = () => {
    setIsOpen(false);
  };

  const clickOutsideCloseRangePicker = (e: MouseEvent | TouchEvent) => {
    const dateRangePicker = document.getElementsByClassName('rs-picker-popup')[0];

    // Check if the click is outside the picker itself
    !dateRangePicker.contains(e.target as any) && closeOnClickOutside && closeRangePicker();
  };

  // Check if the click is outside the picker menu or the trigger button 
  useClickOutside([rangePickerBtnRef, rangePickerMenuRef], clickOutsideCloseRangePicker, isOpen);

  const onSelectDateHandler = (date: Date) => {
    if (shouldSelectOneDate) {
      setCustomSelectionRange([date, date]);
      return;
    }

    setCustomSelectionRange((prevCustomRange) => (
      prevCustomRange.length % 2 === 0 ?
        [date]
        :
        dateSorter('asc', [...prevCustomRange, date]) as [Date, Date]
    ))
  };

  const onCancelHandler = () => {
    setIsOpen(false);
  };

  const onClearHandler = (e: any) => {
    e?.stopPropagation();

    setSelectedRange(defaultRange);
    setAppliedRange(defaultRange);
    setSelectedPreDefinedRange(defaultPreDefinedRange);
    setAppliedPreDefinedRange(defaultPreDefinedRange);
    setIsOpen(false);
    setIsResetable(false);

    onClear?.();
  };

  const onApplyHandler = () => {
    setAppliedRange(selectedRange);
    setAppliedPreDefinedRange(selectedPreDefinedRange);
    setIsOpen(false);
    setIsResetable(true);

    onApply?.(selectedRange, selectedPreDefinedRange?.index, selectedPreDefinedRange?.name, selectedPreDefinedRange?.label);
  };

  const actionBtns: IButton[] = [{
    size: "md",
    text: lang === 'en' ? 'Cancel' : 'الغاء',
    color: "secondary",
    variant: "outlined",
    onClickHandler: () => onCancelHandler(),
  },
  {
    size: "md",
    text: lang === 'en' ? 'Apply' : 'تطبيق',
    color: "primary",
    variant: "contained",
    onClickHandler: () => onApplyHandler(),
    disabled: !selectedRange || customSelectionRange.length % 2 !== 0 || disabledApplyBtn
  }];

  const renderAppliedRange = (range: [Date, Date], lang: 'en' | 'ar' = 'en') => {
    const [startDate, endDate] = range;
    const isNoRange = !startDate && !endDate;

    return isNoRange ? '' : `
    ${formatDateRange(startDate, lang)}
    ${shouldSelectOneDate ? '' : `~ ${formatDateRange(endDate, lang)}`}
    `
  };

  return (
    <div
      className={styles["date-range-picker-wrapper"]}
      data-drp-is-mobile={isMobileBreakpoint}
      data-drp-icon-only-trigger={iconOnlyTrigger}
      style={{ width: width }}
    >
      {label && <div className={styles["date-range-picker-wrapper__label"]}>{label}</div>}
      <DateRangePickerButton
        ref={rangePickerBtnRef}
        placeholder={placeholder}
        text={text || (appliedRange && renderAppliedRange(appliedRange, lang))}
        disabled={disabled}
        onClickHandler={toggleRangePicker}
        {...(resetable && isResetable ? { onClearDateHandler: onClearHandler } : {})}
        iconOnly={iconOnlyTrigger}
        isMobile={isMobileBreakpoint}
      />

      <DateRangePickerMenu
        containerRef={containerRef}
        lang={lang}
        ref={rangePickerMenuRef}
        open={isOpen}
        dateRange={selectedRange}
        disabledDate={selectedPreDefinedRange?.disabledRange || disabledDate}
        onSelectDate={onSelectDateHandler}
        customLocale={customLocale}
        verticalPlacement={verticalPlacement}
        horizontalPlacement={(horizontalPlacement || (isMobileBreakpoint ? 'center' : 'auto'))}
        jumpToStartDateOnShortcutClick={jumpToStartDateOnShortcutClick}
        {...(preDefinedRanges ? { preDefinedRanges: getPreDefinedRanges(selectedPreDefinedRange?.index) } : {})}
        actionBtnsProps={actionBtns}
        rangePickerBtnRef={rangePickerBtnRef}
      />

      {hasBackdrop && isOpen && <div className={styles['date-range-picker-wrapper__backdrop']}></div>}
    </div>
  )
};